<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import './_index.scss'

import type { OnboardingRecommendationFragmentActionable, Props } from './types'
import { SCREEN, SCREEN_CTA } from '@/utils/segment'
import { convertSearchFiltersToQueryObject } from '@/components/SearchFilters/utils'
import AppAiChatBubbles from '@/components/App/Ai/Chat/Bubbles/index.vue'
import ChatOverlaySamPresentation from '@/components/OnboardingContent/ChatOverlay/SamPresentation/index.vue'
import type { OutstandingRecommendation, AppMessage } from '@/composables/useSamGpt/types'

const props = defineProps<Props>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const isLoadingResults = ref(false)
const isReadyToStart = ref(true)
const { $trackPage } = useNuxtApp()
const emit = defineEmits(['finish'])

const handlePresentationEnd = () => {
  isReadyToStart.value = true
}

const handleOutstandingRecommendationClick = async (outstandingRecommendation: OutstandingRecommendation['card_params']) => {
  isLoadingResults.value = true
  await navigateTo(outstandingRecommendation.path)
}

const handlePassiveRecommendationClick = (actionableClicked: OnboardingRecommendationFragmentActionable) => {
  isLoadingResults.value = true

  let legacyStepToRedirect = 3

  if (actionableClicked.filters.opportunityType === 'living') {
    legacyStepToRedirect = 4
  }

  const parsedFilters = convertSearchFiltersToQueryObject(actionableClicked.filters)

  router.push({
    query: {
      ...parsedFilters,
      step: legacyStepToRedirect.toString()
    }
  })

  setTimeout(() => {
    $trackPage()
    emit('finish', actionableClicked.filters)
  }, 500)
}

const handleMessageListChange = (messages: AppMessage[]) => {
  if (!messages.length) return false

  const lastResultMessage = messages.findLast(message => message.outstandingRecommendation)

  if (!lastResultMessage) return false

  let recommendations = false

  if (lastResultMessage.passiveRecommendations) {
    recommendations = lastResultMessage.passiveRecommendations.text_fragments[0].actionable.value
  } else if (lastResultMessage.passiveRecommendationsWithImages) {
    recommendations = lastResultMessage.passiveRecommendationsWithImages
  }

  if (lastResultMessage?.outstandingRecommendation && recommendations) {
    handlePassiveRecommendationClick(recommendations)
  }
}

onMounted(() => {
  const { currentFilters } = props.offlineAgent
  const currentStepIndex = parseInt(route.query.step as string)
  const isInvestingFlow = currentFilters.opportunityType === 'investing' || !currentFilters.opportunityType
  const isLivingFlow = currentFilters.opportunityType === 'living'

  document.body.classList.add('no-background-scroll-with-height')
  document.documentElement.classList.add('no-background-scroll-with-height')

  if ((isInvestingFlow && currentStepIndex === 3) ||
    (isLivingFlow && currentStepIndex === 4)) {
    document.body.classList.remove('no-background-scroll-with-height')
    document.documentElement.classList.remove('no-background-scroll-with-height')
    isReadyToStart.value = true
    isLoadingResults.value = true
    return emit('finish', currentFilters)
  }
})

onUnmounted(() => {
  document.body.classList.remove('no-background-scroll-with-height')
  document.documentElement.classList.remove('no-background-scroll-with-height')
})
</script>

<script lang="ts">
export default {
  name: 'ChatOverlay'
}
</script>

<template>
  <div class="onboarding-chat-overlay h-full backdrop-blur">
    <div
      v-if="!isReadyToStart"
      class="w-full h-full flex items-center justify-center">
      <ChatOverlaySamPresentation @animation-end="handlePresentationEnd" />
    </div>

    <div
      v-if="isLoadingResults"
      class="flex flex-col gap-18 items-center justify-center h-full">
      <div class="spinner" />

      <p class="font-medium text-24 text-center max-w-lg">
        {{ t('screenLoader.title') }}
      </p>
    </div>

    <div
      v-else-if="isReadyToStart"
      class="onboarding-chat-overlay__track h-full">
      <div class="md:max-w-[374px] md:mx-auto mx-24 md:px-0 flex items-center pt-24 pb-28 border-b border-carbon-300">
        <img
          src="https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png?auto=compress,enhance,format"
          class="w-48 h-48 object-cover mr-12"
          alt="Sam">

        <span class="text-20 text-white font-medium">
          Soy Sam AI de La Haus
        </span>
      </div>

      <AppAiChatBubbles
        init-offline
        :screen="SCREEN.INVESTMENT_QUIZ + '_SAM_ONBOARDING'"
        :screen-cta="SCREEN_CTA.INVESTMENT_PRODUCT_BOT"
        :offline-agent
        class="md:max-w-[374px] px-24 md:px-0 mx-auto pt-8 min-h-full pb-120"
        @change-message-list="handleMessageListChange"
        @outstanding-recommendation-click="handleOutstandingRecommendationClick"
        @passive-recommendation-click="handlePassiveRecommendationClick" />
    </div>
  </div>
</template>

<i18n src="./i18n.json" />
